import { useState, useRef, useEffect } from 'react';
import { X, QrCode, Search, Plus, Minus, Trash2, ArrowUpRight, Shirt } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import jsQR from 'jsqr';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GarmentDispatchModal({ onClose, onComplete }) {
  const { token } = useAuth();
  const [step, setStep] = useState('party'); // party, scan, review
  const [partyName, setPartyName] = useState('');
  const [dispatchList, setDispatchList] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [processing, setProcessing] = useState(false);
  
  // Camera refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  const stopCamera = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setScanning(true);
        scanQRCode();
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to access camera');
    }
  };

  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const scan = () => {
      if (!streamRef.current) return;
      
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        
        if (code) {
          handleQRScanned(code.data);
        }
      }
      
      animationRef.current = requestAnimationFrame(scan);
    };
    
    scan();
  };

  const handleQRScanned = async (qrCode) => {
    // Check if already in list
    const existingIndex = dispatchList.findIndex(
      item => item.qr_code?.toLowerCase() === qrCode.toLowerCase()
    );
    
    if (existingIndex >= 0) {
      // Increase quantity for existing item
      const updated = [...dispatchList];
      updated[existingIndex].quantity += 1;
      setDispatchList(updated);
      toast.success(`Increased quantity: ${qrCode}`);
      return;
    }
    
    // Fetch garment item by QR
    try {
      const response = await axios.get(`${API}/garment/qr-scan/${encodeURIComponent(qrCode)}`);
      
      if (!response.data.found) {
        toast.error(`No garment found for: ${qrCode}`);
        return;
      }
      
      const garmentItem = response.data.garment_item;
      const garmentTypes = garmentItem.garment_types || [];
      
      if (garmentTypes.length === 0) {
        toast.error(`No garment types for: ${garmentItem.fabric_name}`);
        return;
      }
      
      // Auto-select first garment type with stock, or just first one
      const defaultType = garmentTypes.find(t => t.quantity_available > 0) || garmentTypes[0];
      
      const newItem = {
        garment_inventory_id: garmentItem.id,
        fabric_name: garmentItem.fabric_name,
        qr_code: garmentItem.qr_code || garmentItem.fabric_code,
        photo_url: garmentItem.photo_url,
        garment_type: defaultType.garment_type,
        available_types: garmentTypes,
        quantity: 1,
        available: defaultType.quantity_available
      };
      
      setDispatchList(prev => [...prev, newItem]);
      toast.success(`Added: ${garmentItem.fabric_name}`);
      
    } catch (error) {
      console.error('QR scan error:', error);
      toast.error('Failed to find garment');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      const response = await axios.get(`${API}/garment/inventory?search=${encodeURIComponent(searchQuery)}&page_size=10`);
      setSearchResults(response.data.items || []);
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    }
  };

  const handleAddFromSearch = async (item) => {
    // Check if already in list
    const existingIndex = dispatchList.findIndex(i => i.garment_inventory_id === item.id);
    
    if (existingIndex >= 0) {
      const updated = [...dispatchList];
      updated[existingIndex].quantity += 1;
      setDispatchList(updated);
      toast.success(`Increased quantity: ${item.fabric_name}`);
      return;
    }
    
    // Fetch full item with types
    try {
      const response = await axios.get(`${API}/garment/inventory/${item.id}/types`);
      const garmentTypes = response.data.garment_types || [];
      
      if (garmentTypes.length === 0) {
        toast.error(`No garment types for: ${item.fabric_name}`);
        return;
      }
      
      const defaultType = garmentTypes.find(t => t.quantity_available > 0) || garmentTypes[0];
      
      const newItem = {
        garment_inventory_id: item.id,
        fabric_name: item.fabric_name,
        qr_code: item.qr_code || item.fabric_code,
        photo_url: item.photo_url,
        garment_type: defaultType.garment_type,
        available_types: garmentTypes,
        quantity: 1,
        available: defaultType.quantity_available
      };
      
      setDispatchList(prev => [...prev, newItem]);
      setSearchResults([]);
      setSearchQuery('');
      toast.success(`Added: ${item.fabric_name}`);
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleUpdateQuantity = (index, delta) => {
    const updated = [...dispatchList];
    const newQty = updated[index].quantity + delta;
    
    if (newQty <= 0) {
      updated.splice(index, 1);
    } else if (newQty <= updated[index].available) {
      updated[index].quantity = newQty;
    } else {
      toast.error(`Only ${updated[index].available} available`);
      return;
    }
    
    setDispatchList(updated);
  };

  const handleUpdateType = (index, newType) => {
    const updated = [...dispatchList];
    const typeInfo = updated[index].available_types.find(t => t.garment_type === newType);
    updated[index].garment_type = newType;
    updated[index].available = typeInfo?.quantity_available || 0;
    
    // Reset quantity if exceeds new available
    if (updated[index].quantity > updated[index].available) {
      updated[index].quantity = Math.max(1, updated[index].available);
    }
    
    setDispatchList(updated);
  };

  const handleRemoveItem = (index) => {
    const updated = [...dispatchList];
    updated.splice(index, 1);
    setDispatchList(updated);
  };

  const handleDispatchAll = async () => {
    if (dispatchList.length === 0) {
      toast.error('No items to dispatch');
      return;
    }
    
    setProcessing(true);
    
    try {
      const items = dispatchList.map(item => ({
        garment_inventory_id: item.garment_inventory_id,
        garment_type: item.garment_type,
        quantity: item.quantity
      }));
      
      const response = await axios.post(`${API}/garment/bulk-dispatch`, {
        party_name: partyName,
        items,
        remarks: null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      onComplete();
    } catch (error) {
      console.error('Dispatch error:', error);
      toast.error(error.response?.data?.detail || 'Dispatch failed');
    } finally {
      setProcessing(false);
    }
  };

  const totalItems = dispatchList.length;
  const totalGarments = dispatchList.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-amber-50 to-orange-50">
          <div>
            <h2 className="text-xl font-bold text-slate-800">Garment Dispatch</h2>
            <p className="text-sm text-slate-500">
              {step === 'party' && 'Step 1: Select Party'}
              {step === 'scan' && `Dispatching to: ${partyName}`}
              {step === 'review' && 'Review & Confirm'}
            </p>
          </div>
          <button onClick={onClose} className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Step 1: Party Selection */}
        {step === 'party' && (
          <div className="p-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Party Name *
            </label>
            <Input
              placeholder="Enter party/client name"
              value={partyName}
              onChange={(e) => setPartyName(e.target.value)}
              className="mb-4"
              autoFocus
            />
            <Button
              onClick={() => setStep('scan')}
              disabled={!partyName.trim()}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              Set Party & Continue
            </Button>
          </div>
        )}

        {/* Step 2: Scan/Add Items */}
        {step === 'scan' && (
          <div className="flex flex-col h-[70vh]">
            {/* Camera/Search Area */}
            <div className="p-4 border-b bg-slate-50">
              <div className="flex gap-2 mb-3">
                <Button
                  variant={scanning ? 'default' : 'outline'}
                  onClick={scanning ? stopCamera : startCamera}
                  className="gap-2"
                >
                  <QrCode className="w-4 h-4" />
                  {scanning ? 'Stop Scanner' : 'Scan QR'}
                </Button>
                <div className="flex-1 flex gap-2">
                  <Input
                    placeholder="Search fabric name or QR..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <Button variant="outline" onClick={handleSearch}>
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Camera View */}
              {scanning && (
                <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                  <video ref={videoRef} className="w-full h-full object-cover" playsInline />
                  <canvas ref={canvasRef} className="hidden" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-48 h-48 border-2 border-white/50 rounded-xl" />
                  </div>
                </div>
              )}

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 bg-white rounded-lg border max-h-40 overflow-y-auto">
                  {searchResults.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleAddFromSearch(item)}
                      className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-0"
                    >
                      {item.photo_url ? (
                        <img src={`${BACKEND_URL}${item.photo_url}`} className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                          <Shirt className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-sm">{item.fabric_name}</p>
                        <p className="text-xs text-slate-500">{item.qr_code}</p>
                      </div>
                      <Plus className="w-5 h-5 text-emerald-600" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Dispatch List */}
            <div className="flex-1 overflow-y-auto p-4">
              {dispatchList.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Shirt className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No items added yet</p>
                  <p className="text-sm">Scan QR codes or search to add garments</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dispatchList.map((item, index) => (
                    <div key={index} className="bg-white rounded-xl border p-3">
                      <div className="flex items-center gap-3">
                        {item.photo_url ? (
                          <img src={`${BACKEND_URL}${item.photo_url}`} className="w-12 h-12 object-cover rounded-lg" />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Shirt className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">{item.fabric_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <select
                              value={item.garment_type}
                              onChange={(e) => handleUpdateType(index, e.target.value)}
                              className="text-xs px-2 py-1 border rounded bg-white"
                            >
                              {item.available_types.map((t) => (
                                <option key={t.garment_type} value={t.garment_type}>
                                  {t.garment_type} ({t.quantity_available})
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateQuantity(index, -1)}
                            className="h-8 w-8 p-0"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-8 text-center font-bold">{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateQuantity(index, 1)}
                            className="h-8 w-8 p-0"
                            disabled={item.quantity >= item.available}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveItem(index)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-slate-50">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-slate-500">Total Items: <span className="font-bold text-slate-800">{totalItems}</span></p>
                  <p className="text-sm text-slate-500">Total Garments: <span className="font-bold text-slate-800">{totalGarments}</span></p>
                </div>
                <Button
                  onClick={() => setStep('party')}
                  variant="outline"
                >
                  Change Party
                </Button>
              </div>
              <Button
                onClick={handleDispatchAll}
                disabled={dispatchList.length === 0 || processing}
                className="w-full bg-amber-600 hover:bg-amber-700 gap-2"
              >
                <ArrowUpRight className="w-5 h-5" />
                {processing ? 'Processing...' : `Dispatch All (${totalGarments} garments)`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
